import { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Archive, RotateCcw, Pencil, GitCompare } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Remark, Role } from '@/types/api';
import { normSeverity } from '@/types/api';
import { api } from '@/api/client';
import { toast } from '@/state/toast';
import { CorrectRemarkModal } from './modals/CorrectRemarkModal';

interface Props {
  remark: Remark;
  active: boolean;
  onClick: () => void;
  /** Роль активного пользователя — определяет видимость контекстного меню. */
  role?: Role;
  /** ID проекта — нужен для PATCH архивации. */
  projectId?: string;
  /** Колбэк «открыть Diff с родителем» — RemarkDetail сам открывает модалку, но если карточка
   *  в gip-канбане без detail-pane, можно передать сюда. */
  onCompareVersions?: (remark: Remark) => void;
}

const SEV_COLOR: Record<string, string> = {
  critical: '#EF4444',
  high:     '#F59E0B',
  medium:   '#6B7280',
  low:      '#9CA3AF',
};
const SEV_LABEL: Record<string, string> = {
  critical: 'Критическое',
  high:     'Высокое',
  medium:   'Среднее',
  low:      'Низкое',
};

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  open:               { label: 'открыто',     color: '#EF4444' },
  awaiting_response:  { label: 'ожидает',     color: '#F59E0B' },
  closed:             { label: 'закрыто',     color: '#10B981' },
};

/** Карточка замечания в списке. Severity показана левой полосой 4px.
 *
 * red-items:
 *  • Архивные карточки рендерим серым/затемнённым, БЕЗ чипа «Архив».
 *  • Контекстное меню (… кнопка) — только для curator и admin. Для expert/gip скрыто.
 */
export function RemarkCard({ remark, active, onClick, role, projectId, onCompareVersions }: Props) {
  const sev = normSeverity(remark.severity);
  const sevColor = SEV_COLOR[sev];
  const sevLabel = SEV_LABEL[sev];
  const isE = remark.defect_type === 'E-gap';
  const typeLabel = isE ? 'Устранимое' : 'Блокирующее';
  const status = remark.remark_status || (remark.closed_at ? 'closed' : 'open');
  const statusInfo = STATUS_LABEL[status];
  const section = remark.section_code || 'не указан';
  const ago = formatAgo(remark.created_at);
  const isArchived = remark.status === 'archived';
  const canManage = role === 'curator' || role === 'admin';

  return (
    <div style={{ position: 'relative', opacity: isArchived ? 0.55 : 1 }}>
      <button
        type="button"
        onClick={onClick}
        className="relative w-full text-left bg-white rounded-card hover:border-ink/20 transition-colors block"
        style={{
          border: active ? '1px solid rgba(10,22,40,.4)' : '1px solid #E5E7EB',
          background: active ? 'rgba(245,158,11,.05)' : (isArchived ? '#F9FAFB' : '#FFFFFF'),
          padding: '14px 14px 14px 18px',
          marginBottom: 8,
          cursor: 'pointer',
          filter: isArchived ? 'grayscale(0.6)' : undefined,
        }}
      >
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: sevColor, borderRadius: '12px 0 0 12px' }} />
        <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: sevColor }}>{sevLabel}</span>
          <span className="text-muted">·</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#0A1628' }}>{typeLabel}</span>
          <span className="text-muted">·</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: statusInfo.color }}>{statusInfo.label}</span>
          {remark.stage === 'acceptance' && (
            <>
              <span className="text-muted">·</span>
              <span className="rounded-pill" style={{ padding: '1px 7px', fontSize: 10, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', background: 'rgba(37,99,235,.10)', color: '#2563EB' }}>Приёмка</span>
            </>
          )}
        </div>
        <div
          className="text-ink mt-2"
          style={{
            fontSize: 13, lineHeight: 1.45,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >{remark.body}</div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-muted" style={{ fontSize: 11 }}>Раздел {section} · {ago}</span>
          {remark.response_count != null && remark.response_count > 0 && (
            <span className="rounded-pill bg-surface text-muted tabular" style={{ padding: '2px 8px', fontSize: 11 }}>
              ↩ {remark.response_count}
            </span>
          )}
        </div>
      </button>

      {canManage && projectId && (
        <CardContextMenu
          remark={remark}
          projectId={projectId}
          onCompareVersions={onCompareVersions}
        />
      )}
    </div>
  );
}

/** Контекстное меню (… кнопка) в правом верхнем углу карточки.
 *  Видно только для curator/admin (см. red-items). */
function CardContextMenu({
  remark, projectId, onCompareVersions,
}: { remark: Remark; projectId: string; onCompareVersions?: (r: Remark) => void }) {
  const [open, setOpen] = useState(false);
  const [correctField, setCorrectField] = useState<'section_code' | 'severity' | 'defect_type' | 'aspect' | 'expert_name' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const isArchived = remark.status === 'archived';

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const archiveMutation = useMutation({
    mutationFn: (next: 'archived' | null) => api.patch(
      `/projects/${projectId}/remarks/${remark.id}`,
      { status: next },
    ),
    onSuccess: (_d, next) => {
      toast.success(next === 'archived' ? 'Замечание архивировано' : 'Восстановлено из архива');
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      setOpen(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось изменить статус'),
  });

  return (
    <div ref={ref} style={{ position: 'absolute', top: 10, right: 10 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Меню замечания"
        style={{
          background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 6,
          width: 28, height: 28, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <MoreHorizontal size={14} stroke="#6B7280" />
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 32, right: 0, zIndex: 25,
            background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8,
            boxShadow: '0 12px 32px rgba(10,22,40,.16)',
            minWidth: 220, padding: 4,
          }}
        >
          <MenuItem
            icon={isArchived ? RotateCcw : Archive}
            label={isArchived ? 'Восстановить из архива' : 'Архивировать'}
            onClick={() => archiveMutation.mutate(isArchived ? null : 'archived')}
            disabled={archiveMutation.isPending}
          />
          <MenuItem icon={Pencil} label="Исправить раздел" onClick={() => { setCorrectField('section_code'); setOpen(false); }} />
          <MenuItem icon={Pencil} label="Исправить важность" onClick={() => { setCorrectField('severity'); setOpen(false); }} />
          <MenuItem icon={Pencil} label="Исправить тип дефекта" onClick={() => { setCorrectField('defect_type'); setOpen(false); }} />
          {remark.parent_remark_id && onCompareVersions && (
            <MenuItem icon={GitCompare} label="Сравнить версии" onClick={() => { onCompareVersions(remark); setOpen(false); }} />
          )}
        </div>
      )}

      {correctField && (
        <CorrectRemarkModal
          projectId={projectId}
          remark={remark}
          field={correctField}
          onClose={() => setCorrectField(null)}
        />
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon, label, onClick, disabled,
}: { icon: typeof Archive; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none', cursor: disabled ? 'wait' : 'pointer',
        fontSize: 13, color: '#0A1628', borderRadius: 6,
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#F3F4F6'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon size={14} stroke="#6B7280" />
      {label}
    </button>
  );
}

export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.floor((now - then) / 1000);
    if (diffSec < 60) return 'только что';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} мин назад`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ч назад`;
    const days = Math.floor(diffSec / 86400);
    if (days < 30) return `${days} дн. назад`;
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}
